import {
  Box,
  Chip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import { getPuppyById } from "../services";
import { useWpFetch } from "../hooks/useWpFetch";
import { useParams } from "react-router";

const statusColors: Record<string, { bg: string; color: string }> = {
  available: { bg: "rgba(182,138,88,0.15)", color: "#6b5b4a" },
  reserved: { bg: "rgba(212,180,131,0.25)", color: "#4a3728" },
  adopted: { bg: "rgba(107,91,74,0.12)", color: "#8a7a6a" },
};

const capitalize = (s: string) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1) : "";

export const SinglePuppy = () => {
  const { id } = useParams<{ id: string }>();
  const { data: details, loading } = useWpFetch(
    () => (id ? getPuppyById(id) : null),
    [id],
    {} as any,
  );

  if (loading) {
    return (
      <Box className="article-main">
        <Skeleton variant="text" width="40%" height={48} sx={{ mb: 1 }} />
        <Skeleton
          variant="rectangular"
          height={300}
          sx={{ borderRadius: 2, mb: 2 }}
        />
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="80%" />
      </Box>
    );
  }

  const imageUrl = details.featured_media || "";
  const photos: string[] = details.photos || [];
  const status = details.status || "";
  const sc = statusColors[status] || statusColors.available;

  const infoFields = [
    { label: "Sex", value: capitalize(details.sex) },
    { label: "Color", value: details.color },
    {
      label: "Status",
      value: status ? (
        <Chip
          label={status.charAt(0).toUpperCase() + status.slice(1)}
          size="small"
          sx={{
            backgroundColor: sc.bg,
            color: sc.color,
            fontWeight: 600,
            fontFamily: '"EB Garamond", serif',
          }}
        />
      ) : null,
    },
    { label: "ID", value: details.custom_id },
  ].filter((f) => f.value);

  const hasDescription = details.description?.replace(/<[^>]*>/g, "").trim();

  return (
    <Box className="article-main">
      {/* Title */}
      <Typography
        variant="h4"
        sx={{
          fontFamily: '"Cinzel", serif',
          color: "#4a3728",
          borderBottom: "2px solid #d4b483",
          pb: 1,
          mb: 3,
        }}
      >
        {details.name}
      </Typography>

      {/* Image + Info */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 3,
          mb: 3,
        }}
      >
        {imageUrl && (
          <Box
            component="img"
            src={imageUrl}
            alt={details.name}
            sx={{
              width: { xs: "100%", md: 320 },
              maxHeight: 400,
              objectFit: "cover",
              borderRadius: 2,
              border: "1px solid #d4c4a8",
            }}
          />
        )}

        {infoFields.length > 0 && (
          <TableContainer sx={{ flex: 1 }}>
            <Table size="small">
              <TableBody>
                {infoFields.map(({ label, value }) => (
                  <TableRow key={label}>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "#4a3728",
                        border: "none",
                        fontFamily: '"Cinzel", serif',
                        width: 120,
                        py: 0.75,
                        pl: 0,
                      }}
                    >
                      {label}
                    </TableCell>
                    <TableCell
                      sx={{ color: "#6b5b4a", border: "none", py: 0.75 }}
                    >
                      {value}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Description */}
      {hasDescription && (
        <Box
          sx={{
            color: "#4a3728",
            lineHeight: 1.8,
            mb: 3,
            "& img": { maxWidth: "100%", borderRadius: 1 },
          }}
          dangerouslySetInnerHTML={{ __html: details.description }}
        />
      )}

      {/* Photo gallery */}
      {photos.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              fontFamily: '"Cinzel", serif',
              color: "#4a3728",
              mb: 1,
            }}
          >
            Photos
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "1fr 1fr 1fr",
              },
              gap: 2,
            }}
          >
            {photos.map((url, i) => (
              <Box
                key={i}
                component="img"
                src={url}
                alt={`${details.name} photo ${i + 1}`}
                sx={{
                  width: "100%",
                  height: 220,
                  objectFit: "cover",
                  borderRadius: 2,
                  border: "1px solid #d4c4a8",
                }}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};
